// EditMembershipPopover — compact in-place CRUD on a PersonCard
// (PRODOPSAI-511 / US8; PRODOPSAI-521 polish iteration;
// PRODOPSAI-523 useEntraTitle support).
//
// Three actions: Change role / Move to another team / Edit note.
// All PATCHes are atomic single-field calls — never delete-then-insert
// (US8 spec: preserves `created_at` + `created_by`). Removal is handled by the
// row's trashcan (PRODOPSAI-539), not here, so there is a single delete path.
//
// Anchored visually under the `[⋯]` button via absolute positioning on the
// PersonCard's relative container. Esc + outside-click both close. The
// popover is rendered as a sibling of the trigger so the floating panel
// inherits the card's layout context without needing a portal.
//
// Helpers live in `./edit-membership-helpers.ts` so SonarCloud S7721 +
// S3776 cannot fire on this file.
//
// PRODOPSAI-521: the team + role native `<select>`s are now
// `SearchableSelect`s, and the role sub-flow gets a bottom `[+ Create new
// role…]` slot that swaps the row in-place to the shared
// `InlineCreateRoleForm` mini-form.
//
// PRODOPSAI-523: the role sub-flow gets a pinned-top "Same as my title"
// row when the membership's Person has an Entra job_title. Picking it
// issues a PATCH `{teamRoleId: null, useEntraTitle: true}` so the
// membership flips over to the dynamic-title binding atomically.

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ITeam,
  ITeamMembershipTreePerson,
  ITeamRole,
} from '@skystone/shared';
import { Button, ErrorBanner, SearchableSelect, Spinner } from '@/components/ui';
import { inputClass } from '@/lib/form-styles';
import { cn, getErrorMessage } from '@/lib/utils';
import { teamApi } from '@/services/team-api';
import { teamMembershipApi } from '@/services/team-membership-api';
import { InlineCreateRoleForm } from './InlineCreateRoleForm';
import {
  buildChangeRoleBody,
  buildEditNoteBody,
  buildMoveTeamBody,
  buildRolePickerOptions,
  buildTeamPickerOptions,
  groupTeamsByRegionLabel,
  isConflictError,
  SAME_AS_MY_TITLE_VALUE,
  type EditAction,
  type MappingRoleOption,
  type MappingTeamOption,
} from './edit-membership-helpers';

/**
 * The role state the popover renders the header from + uses to drive the
 * change-role sub-flow. Either a catalog role binding (`roleId` set) or an
 * entra-title binding (`useEntraTitle: true`). When `useEntraTitle` is true
 * the picker's pinned-top row is preselected so the trigger reads back the
 * current title; when it's false, the catalog role is preselected.
 */
interface MembershipRoleContext {
  roleId: string | null;
  roleLabel: string;
  useEntraTitle: boolean;
  /** Optional — when the linked person has an Entra job_title the picker
   *  surfaces the pinned-top "Same as my title" row. */
  personJobTitle: string | null;
}

interface MembershipContext {
  person: ITeamMembershipTreePerson;
  teamId: string;
  teamName: string;
  role: MembershipRoleContext;
}

interface EditMembershipPopoverProps {
  /** All context needed to render headers + drive the PATCH calls. */
  membership: MembershipContext;
  /** Page-level callback for "membership changed; refetch the tree". */
  onChanged: (message: string) => void;
  /** Close the popover (no state change). */
  onClose: () => void;
}

/** Initial value for the role picker. Mirrors the membership's current
 *  binding so picking the same value submits no-op. */
function initialPickerValue(role: MembershipRoleContext): string {
  if (role.useEntraTitle === true) return SAME_AS_MY_TITLE_VALUE;
  return role.roleId ?? '';
}

export function EditMembershipPopover({
  membership,
  onChanged,
  onClose,
}: Readonly<EditMembershipPopoverProps>) {
  const [action, setAction] = useState<EditAction>('menu');
  const [pickerTeams, setPickerTeams] = useState<ITeam[] | null>(null);
  const [pickerRoles, setPickerRoles] = useState<ITeamRole[] | null>(null);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [creatingRole, setCreatingRole] = useState(false);

  // PATCH/DELETE lifecycle.
  const [busy, setBusy] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [conflictError, setConflictError] = useState<string | null>(null);

  // Sub-flow inputs.
  const [nextRolePickerValue, setNextRolePickerValue] = useState<string>('');
  const [nextTeamId, setNextTeamId] = useState<string>('');
  const [noteInput, setNoteInput] = useState<string>(membership.person.note ?? '');

  const containerRef = useRef<HTMLFieldSetElement>(null);

  // Lazy-load teams + roles only when a sub-flow needs them.
  const ensurePickersLoaded = useCallback(async () => {
    if (pickerTeams !== null && pickerRoles !== null) return;
    setPickerLoading(true);
    setPickerError(null);
    try {
      const [teams, roles] = await Promise.all([
        teamApi.getAll(),
        teamMembershipApi.getTeamRoles(),
      ]);
      setPickerTeams(teams);
      setPickerRoles(roles);
    } catch (err: unknown) {
      setPickerError(getErrorMessage(err, 'Failed to load pickers'));
    } finally {
      setPickerLoading(false);
    }
  }, [pickerTeams, pickerRoles]);

  // Esc closes; outside-click closes. We listen on `mousedown` so the focus
  // shift happens before any nested button click handler fires.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (target === null) return;
      if (containerRef.current === null) return;
      if (containerRef.current.contains(target) === true) return;
      onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [onClose]);

  const enterAction = useCallback(
    (next: EditAction) => {
      setGeneralError(null);
      setConflictError(null);
      setCreatingRole(false);
      if (next === 'change-role') {
        setNextRolePickerValue(initialPickerValue(membership.role));
        ensurePickersLoaded();
      }
      if (next === 'move-team') {
        setNextTeamId(membership.teamId);
        ensurePickersLoaded();
      }
      if (next === 'edit-note') {
        setNoteInput(membership.person.note ?? '');
      }
      setAction(next);
    },
    [ensurePickersLoaded, membership.person.note, membership.role, membership.teamId],
  );

  const handleRoleCreated = useCallback((created: ITeamRole) => {
    setPickerRoles((cur) => {
      if (cur === null) return [created];
      if (cur.some((r) => r.id === created.id) === true) return cur;
      return [...cur, created];
    });
    setNextRolePickerValue(created.id);
    setCreatingRole(false);
  }, []);

  const initialValue = initialPickerValue(membership.role);
  const handleChangeRole = useCallback(async () => {
    if (nextRolePickerValue === '' || nextRolePickerValue === initialValue) return;
    setBusy(true);
    setGeneralError(null);
    setConflictError(null);
    try {
      const body = buildChangeRoleBody(nextRolePickerValue);
      await teamMembershipApi.updateTeamMembership(
        membership.person.membershipId,
        body,
      );
      let newRoleLabel: string;
      if (nextRolePickerValue === SAME_AS_MY_TITLE_VALUE) {
        newRoleLabel = membership.role.personJobTitle ?? '(no title)';
      } else {
        newRoleLabel =
          pickerRoles?.find((r) => r.id === nextRolePickerValue)?.displayName ?? 'role';
      }
      onChanged(`Role changed to ${newRoleLabel}`);
      onClose();
    } catch (err: unknown) {
      if (isConflictError(err) === true) {
        setConflictError(
          `${membership.person.displayName} already has this role on this team`,
        );
      } else {
        setGeneralError(getErrorMessage(err, 'Failed to change role'));
      }
    } finally {
      setBusy(false);
    }
  }, [
    nextRolePickerValue,
    initialValue,
    membership.person.membershipId,
    membership.person.displayName,
    membership.role.personJobTitle,
    pickerRoles,
    onChanged,
    onClose,
  ]);

  const handleMoveTeam = useCallback(async () => {
    if (nextTeamId === '' || nextTeamId === membership.teamId) return;
    setBusy(true);
    setGeneralError(null);
    setConflictError(null);
    try {
      await teamMembershipApi.updateTeamMembership(
        membership.person.membershipId,
        buildMoveTeamBody(nextTeamId),
      );
      const newTeamLabel =
        pickerTeams?.find((t) => t.id === nextTeamId)?.name ?? 'team';
      onChanged(`Moved ${membership.person.displayName} to ${newTeamLabel}`);
      onClose();
    } catch (err: unknown) {
      if (isConflictError(err) === true) {
        const targetName = pickerTeams?.find((t) => t.id === nextTeamId)?.name ?? 'that team';
        setConflictError(
          `${membership.person.displayName} already has this role on ${targetName}. Change role first.`,
        );
      } else {
        setGeneralError(getErrorMessage(err, 'Failed to move team'));
      }
    } finally {
      setBusy(false);
    }
  }, [
    nextTeamId,
    membership.teamId,
    membership.person.membershipId,
    membership.person.displayName,
    pickerTeams,
    onChanged,
    onClose,
  ]);

  const handleEditNote = useCallback(async () => {
    setBusy(true);
    setGeneralError(null);
    try {
      await teamMembershipApi.updateTeamMembership(
        membership.person.membershipId,
        buildEditNoteBody(noteInput),
      );
      onChanged(`Updated note for ${membership.person.displayName}`);
      onClose();
    } catch (err: unknown) {
      setGeneralError(getErrorMessage(err, 'Failed to update note'));
    } finally {
      setBusy(false);
    }
  }, [
    noteInput,
    membership.person.membershipId,
    membership.person.displayName,
    onChanged,
    onClose,
  ]);

  const teamOptions: MappingTeamOption[] =
    pickerTeams === null ? [] : buildTeamPickerOptions(pickerTeams);
  const teamGroups = groupTeamsByRegionLabel(teamOptions);
  const roleOptions: MappingRoleOption[] =
    pickerRoles === null ? [] : buildRolePickerOptions(pickerRoles);

  const teamSearchGroups = teamGroups.map((g) => ({
    label: g.region,
    options: g.teams.map((t) => ({ value: t.value, label: t.label })),
  }));

  // PRODOPSAI-523 (Rabizard #666): show the pinned-top "Same as my title" row
  // when EITHER:
  //   1) the person has an Entra job_title — so a catalog-bound member can
  //      switch to the dynamic binding, OR
  //   2) the membership's CURRENT binding is useEntraTitle (even if the
  //      title is now null) — so the admin can see and preserve the existing
  //      binding rather than being silently forced to pick a catalog role.
  const hasEntraTitle =
    membership.role.personJobTitle !== null && membership.role.personJobTitle.trim() !== '';
  const showSameAsTitleOption = hasEntraTitle || membership.role.useEntraTitle === true;
  const sameAsTitleLabel = hasEntraTitle
    ? `★ Same as my title ("${membership.role.personJobTitle ?? ''}")`
    : '★ Same as my title (no title)';
  const roleSearchOptions = showSameAsTitleOption
    ? [
        {
          value: SAME_AS_MY_TITLE_VALUE,
          label: sameAsTitleLabel,
        },
        ...roleOptions.map((r) => ({ value: r.value, label: r.label })),
      ]
    : roleOptions.map((r) => ({ value: r.value, label: r.label }));

  const headerSubtitle = (() => {
    const teamPart = membership.teamName;
    if (membership.role.useEntraTitle === true) {
      const title = membership.role.personJobTitle ?? '(no title)';
      return `Same as my title ("${title}") · ${teamPart}`;
    }
    return `${membership.role.roleLabel} · ${teamPart}`;
  })();
  const headerName =
    membership.person.isActive === true
      ? membership.person.displayName
      : `${membership.person.displayName} (departed)`;

  // Rabizard #665 finding 2: the [+ Create new role…] row sits inside the
  // role picker but the soft-collision warning that prevents duplicate names
  // is computed from `pickerRoles`. While roles are still loading (or the
  // load failed and pickerRoles is null), every name appears unique → the
  // user can submit a duplicate that the backend rejects with a 409. Hide
  // the create-trigger and show a loading hint until pickerRoles resolves.
  const roleFooter = pickerRoles === null ? (
    <div
      className="px-3 py-2 text-xs text-muted-foreground"
      data-testid="edit-membership-create-role-loading"
    >
      Loading roles…
    </div>
  ) : (
    <button
      type="button"
      onClick={() => setCreatingRole(true)}
      className="w-full rounded-md px-3 py-2 text-left text-sm text-primary hover:bg-muted/50"
      data-testid="edit-membership-create-role-trigger"
    >
      + Create new role…
    </button>
  );

  return (
    <fieldset
      ref={containerRef}
      className={cn(
        'absolute right-0 top-full z-30 mt-1 w-72 rounded-md border border-border',
        'bg-card p-3 text-sm shadow-lg',
      )}
      aria-label="Edit membership"
      data-testid="edit-membership-popover"
    >
      <div className="mb-2 border-b border-border pb-2">
        <p className="font-semibold" data-testid="edit-membership-name">
          {headerName}
        </p>
        <p className="text-xs text-muted-foreground" data-testid="edit-membership-context">
          {headerSubtitle}
        </p>
      </div>

      {generalError !== null && <ErrorBanner message={generalError} />}

      {action === 'menu' && (
        <div className="space-y-1" data-testid="edit-membership-menu">
          <PopoverActionButton
            onClick={() => enterAction('change-role')}
            label="Change role"
            testid="edit-membership-action-change-role"
          />
          <PopoverActionButton
            onClick={() => enterAction('move-team')}
            label="Move to another team"
            testid="edit-membership-action-move-team"
          />
          <PopoverActionButton
            onClick={() => enterAction('edit-note')}
            label="Edit note"
            testid="edit-membership-action-edit-note"
          />
        </div>
      )}

      {action === 'change-role' && (
        <SubFlow
          title="Change role"
          onCancel={() => setAction('menu')}
          onSubmit={handleChangeRole}
          busy={busy}
          submitLabel="Save"
          submitDisabled={nextRolePickerValue === '' || nextRolePickerValue === initialValue}
          showSubmit={creatingRole === false}
        >
          {renderPickerStatus(pickerLoading, pickerError)}
          {creatingRole === true ? (
            <InlineCreateRoleForm
              existingRoles={pickerRoles ?? []}
              onCancel={() => setCreatingRole(false)}
              onCreated={handleRoleCreated}
            />
          ) : (
            <SearchableSelect
              testId="edit-membership-role-select"
              placeholder="Pick a role…"
              options={roleSearchOptions}
              value={nextRolePickerValue}
              onChange={setNextRolePickerValue}
              footer={roleFooter}
            />
          )}
          {conflictError !== null && (
            <p
              className="text-xs text-error-600"
              role="alert"
              data-testid="edit-membership-conflict"
            >
              {conflictError}
            </p>
          )}
        </SubFlow>
      )}

      {action === 'move-team' && (
        <SubFlow
          title="Move to another team"
          onCancel={() => setAction('menu')}
          onSubmit={handleMoveTeam}
          busy={busy}
          submitLabel="Move"
          submitDisabled={nextTeamId === '' || nextTeamId === membership.teamId}
        >
          {renderPickerStatus(pickerLoading, pickerError)}
          <SearchableSelect
            testId="edit-membership-team-select"
            placeholder="Pick a team…"
            groups={teamSearchGroups}
            value={nextTeamId}
            onChange={setNextTeamId}
          />
          {conflictError !== null && (
            <p
              className="text-xs text-error-600"
              role="alert"
              data-testid="edit-membership-conflict"
            >
              {conflictError}
            </p>
          )}
        </SubFlow>
      )}

      {action === 'edit-note' && (
        <SubFlow
          title="Edit note"
          onCancel={() => setAction('menu')}
          onSubmit={handleEditNote}
          busy={busy}
          submitLabel="Save"
          submitDisabled={false}
        >
          <input
            type="text"
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            maxLength={255}
            placeholder="e.g. PL, part time"
            className={inputClass}
            data-testid="edit-membership-note-input"
          />
        </SubFlow>
      )}

    </fieldset>
  );
}

interface PopoverActionButtonProps {
  onClick: () => void;
  label: string;
  destructive?: boolean;
  testid: string;
}

function PopoverActionButton({
  onClick,
  label,
  destructive,
  testid,
}: Readonly<PopoverActionButtonProps>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/50',
        destructive === true && 'text-destructive hover:bg-error-50',
      )}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

interface SubFlowProps {
  title: string;
  onCancel: () => void;
  onSubmit: () => void | Promise<void>;
  busy: boolean;
  submitLabel: string;
  submitDisabled: boolean;
  children: React.ReactNode;
  /** When false, the Save/Move button row is hidden (used while the inline
   * create-role mini-form has taken over the sub-flow content). */
  showSubmit?: boolean;
}

function SubFlow({
  title,
  onCancel,
  onSubmit,
  busy,
  submitLabel,
  submitDisabled,
  children,
  showSubmit = true,
}: Readonly<SubFlowProps>) {
  return (
    <div className="space-y-2" data-testid={`edit-membership-subflow-${title.toLowerCase().replaceAll(' ', '-')}`}>
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      {children}
      {showSubmit === true && (
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void onSubmit()}
            isLoading={busy}
            disabled={submitDisabled}
            data-testid="edit-membership-subflow-submit"
          >
            {submitLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

function renderPickerStatus(loading: boolean, error: string | null) {
  if (loading === true) {
    return (
      <div className="flex items-center justify-center py-2" data-testid="edit-membership-picker-loading">
        <Spinner size="sm" />
      </div>
    );
  }
  if (error !== null) {
    return <ErrorBanner message={error} />;
  }
  return null;
}
